import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import api from '../../../lib/api';
import Chip from '../../../components/Chip';
import { locationTypeShort, originLabel, originVariant } from '../../../constants/votingLocations';
import { SuccessIcon } from '../../../components/icons';

interface ByElection {
  by_type: Array<{ location_type: string; origin: string; n: number }>;
  counties_covered: number;
  counties_total: number;
  counties_missing: string[];
  needs_attention: Array<{
    id: string;
    display_name: string;
    county_name: string;
    display_name_source: string;
    county_district_id: string | null;
  }>;
  non_import_rows: Array<{
    id: string;
    location_type: string;
    origin: string;
    display_name: string;
    county_name: string;
  }>;
  has_imported_rows: boolean;
}

/**
 * Post-import QA for one election (PRD §5.4). Answers "did the import work?"
 * against the data itself, rather than leaving it in a job-history summary
 * nobody opens — county coverage, what the importer had to guess, and which
 * rows are not the state's.
 */
const ElectionLocationsSummary: React.FC<{
  electionCalendarId: string;
  cycleName: string | null;
}> = ({ electionCalendarId, cycleName }) => {
  const summary = useQuery({
    queryKey: ['admin-voting-locations', 'by-election', electionCalendarId],
    queryFn: async () => {
      const response = await api.get('/admin/voting-locations/by-election', {
        params: { electionCalendarId },
      });
      return response.data.data as ByElection;
    },
  });

  if (summary.isLoading) {
    return (
      <div className="card p-4 text-sm text-text-secondary dark:text-text-dark-secondary">
        Checking this election's locations…
      </div>
    );
  }
  if (summary.isError || !summary.data) return null;

  const data = summary.data;
  const totals = new Map<string, number>();
  for (const row of data.by_type) {
    totals.set(row.location_type, (totals.get(row.location_type) ?? 0) + row.n);
  }
  const coverageComplete = data.counties_covered === data.counties_total;
  const projectedCount = data.non_import_rows.filter((row) => row.origin === 'projected').length;
  const manualCount = data.non_import_rows.filter((row) => row.origin === 'manual').length;

  /**
   * Nothing loaded at all is a different situation from a partial import, and
   * naming all 64 counties as "missing" buries that under a wall of text. The
   * empty case gets one line saying what to do instead.
   */
  const isEmpty = data.counties_covered === 0 && totals.size === 0;

  if (isEmpty) {
    return (
      <div className="card flex items-start gap-2 p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600 dark:text-warning-400" />
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          <span className="font-medium text-text-primary dark:text-text-dark-primary">
            No locations in {cycleName || 'this cycle'} yet.
          </span>{' '}
          Copy them forward from another election, or import the state's file for this one.
        </p>
      </div>
    );
  }

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
          {cycleName || 'This cycle'}
        </h3>
        {!data.has_imported_rows && (
          <Chip label="Projected — no state file imported yet" variant="warning" size="sm" />
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {[...totals.entries()].map(([locationType, n]) => (
          <Chip key={locationType} label={`${locationTypeShort(locationType)}: ${n}`} variant="neutral" size="sm" />
        ))}
      </div>

      <div className="flex items-start gap-2 text-sm">
        {coverageComplete ? (
          <SuccessIcon className="mt-0.5 h-4 w-4 shrink-0 text-success-600 dark:text-success-400" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600 dark:text-warning-400" />
        )}
        <div className="text-text-secondary dark:text-text-dark-secondary">
          <span className="font-medium text-text-primary dark:text-text-dark-primary">
            {data.counties_covered} of {data.counties_total} counties
          </span>
          {/* A handful of gaps is worth naming; a long list is just noise, so
              past a point say how many rather than which. */}
          {!coverageComplete && data.counties_missing.length > 0 && (
            <span>
              {data.counties_missing.length <= 8
                ? ` — missing: ${data.counties_missing.join(', ')}`
                : ` — ${data.counties_missing.length} counties have no locations yet`}
            </span>
          )}
        </div>
      </div>

      {data.needs_attention.length > 0 && (
        <div className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600 dark:text-warning-400" />
          <div className="text-text-secondary dark:text-text-dark-secondary">
            <span className="font-medium text-text-primary dark:text-text-dark-primary">
              {data.needs_attention.length} place{data.needs_attention.length === 1 ? '' : 's'} the import had to guess at
            </span>
            {' — '}
            {data.needs_attention.filter((p) => p.display_name_source === 'address').length} named from an address,
            {' '}
            {data.needs_attention.filter((p) => p.county_district_id === null).length} with an unmapped county.
          </div>
        </div>
      )}

      {data.non_import_rows.length > 0 && (
        <div className="flex items-start gap-2 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary dark:text-text-dark-tertiary" />
          <div className="text-text-secondary dark:text-text-dark-secondary">
            {manualCount > 0 && (
              <div>
                <Chip label={originLabel('manual')} variant={originVariant('manual')} size="sm" />{' '}
                <span className="font-medium text-text-primary dark:text-text-dark-primary">
                  {manualCount} kept through the last import
                </span>{' '}
                — not in the state's file. Worth reviewing each cycle so closed sites don't linger.
              </div>
            )}
            {projectedCount > 0 && (
              <div className={manualCount > 0 ? 'mt-1' : undefined}>
                <Chip label={originLabel('projected')} variant={originVariant('projected')} size="sm" />{' '}
                <span className="font-medium text-text-primary dark:text-text-dark-primary">
                  {projectedCount} placeholder{projectedCount === 1 ? '' : 's'}
                </span>{' '}
                — copied from another cycle, replaced automatically when a real file is imported.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectionLocationsSummary;
